import {
    fetchUserWithLocation,
    lookupUserByEmail
} from "../../users/services/UsersService.ts";
import {convertFromTMSUserToUser} from "../../users/utils/converter.ts";
import {hasIdProvider} from "../../federated-id-providers/services/FederatedIdProviderService.tsx";
import {NEXTCognitoUser} from "../../../types/cognito.ts";
import {
    HandoffClient,
    ResolvedAccount
} from "../types.ts";
import {
    HANDOFF_ERROR_CODES,
    HandoffError
} from "./HandoffError.ts";

const lookupUser = async (email: string): Promise<NEXTCognitoUser | null> => {
    const {data, status} = await lookupUserByEmail(email);
    if (status !== 302 || !data.location) {
        return null;
    }
    return fetchUserWithLocation(data.location);
};

// Read-only tenant-management lookup; never touches the account list.
// Federated detection and IdP choice follow the legacy rule (hasIdProvider, first federated-id-providers entry).
export const resolveAccountForEmail = async (clients: HandoffClient[], email: string): Promise<ResolvedAccount> => {
    let user: NEXTCognitoUser | null;
    try {
        user = await lookupUser(email);
    } catch (e) {
        if (clients.length !== 1) {
            throw e;
        }
        user = null;
    }

    if (user === null) {
        if (clients.length === 1) {
            return {kind: 'password', client: clients[0]};
        }
        throw new HandoffError(HANDOFF_ERROR_CODES.accountNotFound);
    }

    const account = convertFromTMSUserToUser(user, email);
    const poolId = account.cognitoUserPoolId;

    if (hasIdProvider(account)) {
        const identityProvider = account.nextcloudIdentityProvider?.name;
        if (!poolId || !identityProvider) {
            throw new HandoffError(HANDOFF_ERROR_CODES.accountNotFound);
        }
        return {kind: 'federated', poolId, identityProvider};
    }

    const match = clients.find((c) => c.poolId === poolId);
    if (!match) {
        throw new HandoffError(HANDOFF_ERROR_CODES.poolNotAllowed);
    }
    return {kind: 'password', client: match};
};
